import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Image,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../../constants/theme';
import { useFoodStore, Product } from '../../store/useFoodStore';
import { firestoreService } from '../../services/firestoreService';
import { volver } from '../../utils/volver';
import { pesos } from '../../utils/dinero';
import { ratingDisplay } from '../../services/ratingUtils';

/**
 * Detalle de restaurante.
 *
 * REGLA: lectura directa de /restaurants/{id} en Firestore vía listener.
 * Así soporta deep-link desde push notifications (FOOD_ORDER_DISPATCH,
 * promos, etc.) sin depender del listing previo en useFoodStore.
 */
export default function RestaurantDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const addToCart = useFoodStore((state) => state.addToCart);
  const cart = useFoodStore((state) => state.cart);
  // Para saber si el carrito ya es de OTRO negocio antes de prometer nada.
  const cartRestaurantId = useFoodStore((state) => state.cartRestaurantId);
  const clearCart = useFoodStore((state) => state.clearCart);

  const [restaurant, setRestaurant] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    const unsub = firestoreService.listenToRestaurantById(id, (data) => {
      setRestaurant(data);
      setLoading(false);
    });
    return () => unsub();
  }, [id]);

  if (loading) {
    return (
      <SafeAreaView style={[styles.safeArea, { justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator size="large" color={Colors.primary} />
      </SafeAreaView>
    );
  }

  if (!restaurant) {
    return (
      <SafeAreaView style={[styles.safeArea, { justifyContent: 'center', alignItems: 'center' }]}>
        <Ionicons name="storefront-outline" size={60} color={Colors.border} />
        <Text style={{ marginTop: 12, color: Colors.textSecondary }}>
          Negocio no encontrado.
        </Text>
        <TouchableOpacity onPress={() => volver(router)} style={{ marginTop: 20 }}>
          <Text style={{ color: Colors.primary, fontWeight: '600' }}>Volver</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  if (restaurant.status && restaurant.status !== 'APPROVED') {
    return (
      <SafeAreaView style={[styles.safeArea, { justifyContent: 'center', alignItems: 'center', padding: 20 }]}>
        <Ionicons name="time-outline" size={60} color={Colors.textSecondary} />
        <Text style={{ marginTop: 12, color: Colors.text, fontWeight: '700', fontSize: 17 }}>
          Negocio en revisión
        </Text>
        <Text style={{ color: Colors.textSecondary, marginTop: 6, textAlign: 'center' }}>
          Este negocio aún no está disponible para recibir pedidos.
        </Text>
        <TouchableOpacity onPress={() => volver(router)} style={{ marginTop: 20 }}>
          <Text style={{ color: Colors.primary, fontWeight: '600' }}>Volver</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  /**
   * ⚠️ ESTO DECIA «AÑADIDO» SIN HABER AÑADIDO NADA.
   *
   * `addToCart` descarta el producto EN SILENCIO cuando el carrito ya es de
   * otro negocio: `if (state.cartRestaurantId !== restaurantId) { console.warn(
   * 'Solo puedes pedir de un restaurante a la vez'); return state; }`
   * (store/useFoodStore.ts). El propio comentario del store lo admitia: «En una
   * app real podriamos mostrar una alerta preguntando si quiere limpiar el
   * carrito».
   *
   * Y aqui se lanzaba el Alert de «Añadido» justo despues, sin mirar el
   * resultado. Lo que vivia el cliente: tenia una pizza de otro sitio, tocaba
   * el + de una hamburguesa, leia «Añadido — Hamburguesa Doble agregado al
   * carrito», tocaba «Ver Carrito»… y solo estaba la pizza. Podia repetirlo
   * diez veces y diez veces le decia que si.
   *
   * Ahora se mira ANTES: si el carrito es de otro negocio se le pregunta, con
   * el nombre de los dos, y solo se anuncia lo que de verdad paso.
   */
  const anunciarAnadido = (product: Product) => {
    Alert.alert('Añadido', `${product.name} agregado al carrito.`, [
      { text: 'Seguir comprando', style: 'cancel' },
      { text: 'Ver Carrito', onPress: () => router.push('/cart') },
    ]);
  };

  const handleAddToCart = (product: Product) => {
    const esDeOtroNegocio = !!cartRestaurantId && cartRestaurantId !== restaurant.id;

    if (esDeOtroNegocio) {
      Alert.alert(
        'Ya tienes un pedido empezado',
        `Tu carrito es de otro negocio. Solo se puede pedir de uno a la vez.\n\n¿Vaciarlo y empezar tu pedido en ${restaurant.name ?? 'este negocio'}?`,
        [
          { text: 'No, seguir con el otro', style: 'cancel' },
          {
            text: 'Vaciar y empezar aquí',
            style: 'destructive',
            onPress: () => {
              clearCart();
              addToCart(product, restaurant.id, 1);
              anunciarAnadido(product);
            },
          },
        ]
      );
      return;
    }

    addToCart(product, restaurant.id, 1);
    anunciarAnadido(product);
  };

  const cartItemsCount = cart.reduce((total, item) => total + item.quantity, 0);
  const cartTotal = useFoodStore.getState().getCartTotal();
  const menu: Product[] = Array.isArray(restaurant.menu) ? restaurant.menu : [];

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView showsVerticalScrollIndicator={false} bounces={false}>
        {/* PORTADA Y BACK BUTTON */}
        <View style={styles.coverContainer}>
          {restaurant.coverUrl ? (
            <Image source={{ uri: restaurant.coverUrl }} style={styles.coverImage} />
          ) : (
            <View style={[styles.coverImage, { backgroundColor: '#EEE', justifyContent: 'center', alignItems: 'center' }]}>
              <Ionicons name="restaurant-outline" size={64} color={Colors.border} />
            </View>
          )}
          <View style={styles.overlay} />
          <TouchableOpacity style={styles.backButton} onPress={() => volver(router)}>
            <Ionicons name="arrow-back" size={24} color="#FFF" />
          </TouchableOpacity>
        </View>

        {/* INFO DEL RESTAURANTE */}
        <View style={styles.infoContainer}>
          <Text style={styles.restaurantName}>{restaurant.name ?? 'Negocio'}</Text>
          {restaurant.description ? (
            <Text style={styles.restaurantDesc}>{restaurant.description}</Text>
          ) : null}

          <View style={styles.statsRow}>
            {/* ── NADA DE CINCO ESTRELLAS DE RELLENO ─────────────────────────
                Antes esto imprimía literalmente "5.0 (0+)": cinco estrellas al
                lado de un contador que decía cero, y el 5.0 no venía de ninguna
                calificación — lo escribía la pantalla de registro del
                restaurante (`restaurant-registration.tsx`), que ya no lo
                guarda. Ahora manda el mismo ayudante que usa el resto de la
                app: si no hay ni una nota real, se dice "Nuevo" (igual que la
                tarjeta de oferta) en vez de inventar una nota. */}
            {(() => {
              const r = ratingDisplay(restaurant.rating, restaurant.restaurantRatingCount);
              if (r.isNew) {
                return (
                  <View style={styles.statItem}>
                    <View style={styles.nuevoChip}>
                      <Text style={styles.nuevoChipText}>Nuevo</Text>
                    </View>
                  </View>
                );
              }
              return (
                <View style={styles.statItem}>
                  <Ionicons name="star" size={16} color="#FF9500" />
                  <Text style={styles.statText}>
                    {r.label} ({restaurant.restaurantRatingCount})
                  </Text>
                </View>
              );
            })()}
            {/* ── UN TIEMPO QUE NADIE PROMETIÓ ──────────────────────────
                El respaldo era '20-30 min': si el restaurante no puso su
                tiempo de entrega, la app se lo inventaba y el cliente pedía
                contando con media hora. Ese número no sale de ningún dato —
                ni del restaurante, ni de una medición, ni del ETA real. Sin
                dato se dice sin dato. (El `.trim()` es porque un texto vacío
                pasa por `??` como si fuera un valor.) */}
            <View style={styles.statItem}>
              <Ionicons name="time-outline" size={16} color={Colors.textSecondary} />
              <Text style={styles.statText}>
                {String(restaurant.deliveryTime ?? '').trim() || 'Sin tiempo indicado'}
              </Text>
            </View>
            <View style={styles.statItem}>
              <Ionicons name="bicycle" size={16} color={Colors.textSecondary} />
              <Text style={styles.statText}>{pesos(restaurant.deliveryFee ?? 0)}</Text>
            </View>
          </View>
        </View>

        {/* MENU */}
        <View style={styles.menuContainer}>
          <Text style={styles.sectionTitle}>Menú</Text>

          {menu.length === 0 ? (
            <View style={{ alignItems: 'center', paddingVertical: 30 }}>
              <Ionicons name="fast-food-outline" size={40} color={Colors.border} />
              <Text style={{ color: Colors.textSecondary, marginTop: 8 }}>
                Este negocio aún no publicó sus productos.
              </Text>
            </View>
          ) : (
            menu.map((product: any, idx: number) => {
              // Soporta tanto `images: string[]` (schema nuevo) como `imageUrl` (legacy)
              const imgs: string[] = Array.isArray(product.images)
                ? product.images.filter(Boolean)
                : product.imageUrl
                  ? [product.imageUrl]
                  : [];
              const primary = imgs[0] ?? null;
              const isOut = product.isAvailable === false;
              return (
                <View
                  key={product.id ?? idx}
                  style={[styles.productCard, isOut && { opacity: 0.55 }]}
                >
                  <View style={styles.productInfo}>
                    <Text style={styles.productName}>{product.name}</Text>
                    {product.description ? (
                      <Text style={styles.productDesc} numberOfLines={2}>
                        {product.description}
                      </Text>
                    ) : null}
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
                      <Text style={styles.productPrice}>{pesos(product.price)}</Text>
                      {product.prepTime && (
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 10 }}>
                          <Ionicons name="time-outline" size={11} color={Colors.textSecondary} />
                          <Text style={{ fontSize: 11, color: Colors.textSecondary, marginLeft: 3 }}>
                            {product.prepTime}
                          </Text>
                        </View>
                      )}
                      {isOut && (
                        <View style={{
                          backgroundColor: '#FFEBEB',
                          paddingHorizontal: 6, paddingVertical: 2,
                          borderRadius: 6, marginLeft: 8,
                        }}>
                          <Text style={{ fontSize: 10, fontWeight: '700', color: '#D32F2F' }}>AGOTADO</Text>
                        </View>
                      )}
                    </View>
                  </View>
                  <View style={styles.productRight}>
                    {primary ? (
                      <View style={{ position: 'relative' }}>
                        <Image
                          source={{ uri: primary }}
                          style={styles.productImage}
                          resizeMode="cover"
                        />
                        {imgs.length > 1 && (
                          <View style={styles.imgCountBadge}>
                            <Ionicons name="images" size={10} color="#FFF" />
                            <Text style={styles.imgCountText}>{imgs.length}</Text>
                          </View>
                        )}
                      </View>
                    ) : (
                      <View style={[styles.productImage, {
                        backgroundColor: '#F8F8F8',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderWidth: 1,
                        borderColor: '#EEE',
                      }]}>
                        <Ionicons name="restaurant-outline" size={28} color={Colors.border} />
                      </View>
                    )}
                    <TouchableOpacity
                      style={[
                        styles.addButton,
                        isOut && { backgroundColor: '#E0E0E0', shadowOpacity: 0 },
                      ]}
                      onPress={() => !isOut && handleAddToCart(product)}
                      disabled={isOut}
                      activeOpacity={0.85}
                    >
                      <Ionicons
                        name="add"
                        size={22}
                        color={isOut ? '#999' : '#FFF'}
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })
          )}
        </View>
      </ScrollView>

      {/* FLOATING CART BUTTON */}
      {cartItemsCount > 0 && (
        <View style={styles.floatingCartContainer}>
          <TouchableOpacity style={styles.floatingCartBtn} onPress={() => router.push('/cart')}>
            <View style={styles.cartBadge}>
              <Text style={styles.cartBadgeText}>{cartItemsCount}</Text>
            </View>
            <Text style={styles.viewCartText}>Ver Carrito</Text>
            <Text style={styles.cartTotalText}>{pesos(cartTotal)}</Text>
          </TouchableOpacity>
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#FFF' },
  coverContainer: { height: 220, position: 'relative' },
  coverImage: { width: '100%', height: '100%' },
  overlay: { ...StyleSheet.absoluteFill as any, backgroundColor: 'rgba(0,0,0,0.3)' },
  backButton: { position: 'absolute', top: 40, left: 20, backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: 20, padding: 8 },

  infoContainer: { padding: SIZES.padding, backgroundColor: '#FFF', borderTopLeftRadius: 24, borderTopRightRadius: 24, marginTop: -20 },
  restaurantName: { fontSize: 24, fontWeight: 'bold', color: Colors.text },
  restaurantDesc: { fontSize: 14, color: Colors.textSecondary, marginTop: 5 },
  statsRow: { flexDirection: 'row', marginTop: 15, borderBottomWidth: 1, borderBottomColor: '#F0F0F0', paddingBottom: 15 },
  nuevoChip: {
    backgroundColor: 'rgba(255,149,0,0.15)',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
  },
  nuevoChipText: {
    fontSize: 11,
    fontWeight: '800',
    color: '#FF9500',
  },
  statItem: { flexDirection: 'row', alignItems: 'center', marginRight: 20 },
  statText: { fontSize: 13, color: Colors.textSecondary, marginLeft: 4, fontWeight: '500' },

  menuContainer: { paddingHorizontal: SIZES.padding, paddingBottom: 100 },
  sectionTitle: { fontSize: 20, fontWeight: 'bold', color: Colors.text, marginVertical: 15 },

  productCard: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#F2F2F2' },
  productInfo: { flex: 1, paddingRight: 15, justifyContent: 'center' },
  productName: { fontSize: 16, fontWeight: '700', color: Colors.text },
  productDesc: { fontSize: 13, color: Colors.textSecondary, marginTop: 5, lineHeight: 18 },
  productPrice: { fontSize: 15, fontWeight: '800', color: Colors.primary },
  productRight: { alignItems: 'flex-end', justifyContent: 'center' },
  productImage: {
    width: 110, height: 110, borderRadius: 14,
    backgroundColor: '#F5F5F5',
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 5,
    elevation: 2,
  },
  imgCountBadge: {
    position: 'absolute', top: 6, right: 6,
    backgroundColor: 'rgba(0,0,0,0.65)',
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 6, paddingVertical: 3,
    borderRadius: 10,
  },
  imgCountText: { fontSize: 10, color: '#FFF', marginLeft: 3, fontWeight: '700' },
  addButton: {
    position: 'absolute', bottom: -8, right: 8,
    backgroundColor: Colors.primary,
    width: 38, height: 38, borderRadius: 19,
    justifyContent: 'center', alignItems: 'center',
    shadowColor: Colors.primary,
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.35,
    shadowRadius: 5,
    elevation: 5,
    borderWidth: 2, borderColor: '#FFF',
  },

  floatingCartContainer: { position: 'absolute', bottom: 30, left: 20, right: 20 },
  floatingCartBtn: { backgroundColor: Colors.primary, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 15, borderRadius: SIZES.radius, shadowColor: Colors.primary, shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 8, elevation: 6 },
  cartBadge: { backgroundColor: '#FFF', width: 28, height: 28, borderRadius: 14, justifyContent: 'center', alignItems: 'center' },
  cartBadgeText: { color: Colors.primary, fontWeight: 'bold', fontSize: 14 },
  viewCartText: { color: '#FFF', fontWeight: 'bold', fontSize: 16 },
  cartTotalText: { color: '#FFF', fontWeight: 'bold', fontSize: 16 },
});
